import { useState, useMemo } from 'react';
import type { JobTypeVersion } from '../api/client';
import type { JobTypeVersionContent } from '@capture/shared';
import { validateContent, flattenToLegacyContent, migrateContentToV3 } from '@capture/shared';
import './HistoryTab.css';

export interface CherryPickSelection {
  fieldNames?: string[];
  pipeline?: boolean;
  headerMapping?: boolean;
  ruleIds?: string[];
  destinationIds?: string[];
}

interface CherryPickModalProps {
  sourceVersion: JobTypeVersion;
  draftContent: JobTypeVersionContent;
  onApply: (selection: CherryPickSelection) => void;
  onCancel: () => void;
}

export function CherryPickModal({ sourceVersion, draftContent, onApply, onCancel }: CherryPickModalProps) {
  const [selection, setSelection] = useState<CherryPickSelection>({ fieldNames: [] });

  // F-037 — `sourceVersion.content` is raw stored content and can be V3
  // (per-form), where `.fields` lives under forms[]. Flatten to the legacy view
  // before reading top-level `.fields`. (`draftContent` is already store-flattened.)
  const sourceLegacy = useMemo(
    () => flattenToLegacyContent(migrateContentToV3(sourceVersion.content as unknown as JobTypeVersionContent)),
    [sourceVersion],
  );

  const projectedErrors = useMemo(() => {
    const picked = new Set(selection.fieldNames ?? []);
    const draftFieldsByName = new Map(draftContent.fields.map((f) => [f.name, f]));
    const sourceFieldsByName = new Map(sourceLegacy.fields.map((f) => [f.name, f]));
    for (const name of picked) {
      const s = sourceFieldsByName.get(name);
      if (s) draftFieldsByName.set(name, s);
    }
    const projected: JobTypeVersionContent = {
      ...draftContent,
      fields: Array.from(draftFieldsByName.values()),
    };
    return validateContent(projected).filter((e) => e.code === 'behavior.ref.missing');
  }, [selection, sourceLegacy, draftContent]);

  function toggleField(name: string) {
    const cur = new Set(selection.fieldNames ?? []);
    if (cur.has(name)) cur.delete(name); else cur.add(name);
    setSelection({ ...selection, fieldNames: Array.from(cur) });
  }

  function addMissingRefs() {
    const missing = new Set<string>();
    for (const e of projectedErrors) {
      const m = e.message.match(/field "([^"]+)"/);
      if (m?.[1]) missing.add(m[1]);
    }
    const merged = new Set([...(selection.fieldNames ?? []), ...missing]);
    setSelection({ ...selection, fieldNames: Array.from(merged) });
  }

  return (
    <div className="cherry-pick-modal" role="dialog" aria-modal="true">
      <h2>Cherry-pick from v{sourceVersion.versionNumber}</h2>

      <fieldset>
        <legend>Fields</legend>
        {sourceLegacy.fields.map((f) => (
          <label key={f.fieldDefinitionId}>
            <input type="checkbox" checked={(selection.fieldNames ?? []).includes(f.name)} onChange={() => toggleField(f.name)} />
            {f.name}
          </label>
        ))}
      </fieldset>

      <label>
        <input
          type="checkbox"
          checked={selection.pipeline ?? false}
          onChange={(e) => setSelection({ ...selection, pipeline: e.target.checked })}
        />
        Replace pipeline
      </label>
      <label>
        <input
          type="checkbox"
          checked={selection.headerMapping ?? false}
          onChange={(e) => setSelection({ ...selection, headerMapping: e.target.checked })}
        />
        Replace header mapping
      </label>

      {projectedErrors.length > 0 && (
        <div className="cherry-pick-modal__warning" role="alert">
          <p>Picking this selection leaves dangling references:</p>
          <ul>
            {projectedErrors.map((e, i) => <li key={i}>{e.message}</li>)}
          </ul>
          <button type="button" onClick={addMissingRefs}>Add the missing fields</button>
        </div>
      )}

      <div className="cherry-pick-modal__actions">
        <button type="button" onClick={onCancel}>Cancel</button>
        <button type="button" onClick={() => onApply(selection)}>Apply</button>
      </div>
    </div>
  );
}
